import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {withRouter, Redirect} from 'react-router-dom';
import logoAssets from 'assets/assets.js';
import {logout} from 'actions/login';

import Faq from 'components/Faq';
import Login from 'components/Login';
import Coverage from 'components/Coverage';
import Footer from 'components/Footer';
import Header from 'components/Header';
import {RedirectModal, LoginModal} from 'components/Modals';
import LoginModals from 'components/Login/LoginModals';
import {
  hasLoginV2Workflow,
  isNpiModalType,
  loginV2NpiUsesLegacyHcpGate,
  requiresAuthentication,
} from 'services/authentication';
import {useLinkTracking} from 'services/analytics/useLinkTracking';

import './styles.scss';
import DynamicCoverage from '../../components/DynamicCoverage';

const LinkTrackingListener = () => {
  useLinkTracking();
  return null;
}

const localStorageBoxedIsiExpanded = localStorage.getItem('boxedIsi-expanded');

export class PalPage extends Component {
  constructor(props) {
    super(props);

    const {settings} = props;

    const isExpandedInitialState = localStorageBoxedIsiExpanded !== null ?
      localStorageBoxedIsiExpanded === 'true' : settings.isi.boxedIsi.initializeExpanded;

    this.state = {
      showLoginModal: false,
      showRedirectModal: false,
      authenticated: false,
      isIsiTrayExpanded: isExpandedInitialState
    };

    this._modalHandler = this._modalHandler.bind(this);
  }

  updateIsiTrayExpanded = (value) => {
    localStorage.setItem('boxedIsi-expanded', value);
    this.setState((prevState) => ({
      ...prevState,
      isIsiTrayExpanded: value,
    }));
  }

  toggleExpandBoxedIsi = () => {
    this.updateIsiTrayExpanded(!this.state.isIsiTrayExpanded);
  }

  _modalHandler(type) {
    switch (type) {
      case 'hideLogin':
        this.setState({showLoginModal: false});
        // LoginV2 + npi + HCP: after HCP accept on any route other than /login itself,
        // send the user to /login for the NPI form. The sessionStorage flag lets
        // componentDidMount skip re-opening the gate for this one navigation only;
        // a page refresh clears it (sessionStorage survives reloads, but we consume
        // it immediately on mount so subsequent refreshes see nothing).
        if (
          loginV2NpiUsesLegacyHcpGate(this.props.settings) &&
          this.props.location.pathname !== '/login' &&
          requiresAuthentication(this.props.settings)
        ) {
          sessionStorage.setItem('hcpJustAccepted', '1');
          this.props.history.push({pathname: '/login', search: this.props.location.search});
        }
        break;

      case 'redirect':
        this.setState({showLoginModal: false, showRedirectModal: true});
        break;

      case 'logout':
        this.props.dispatchLogout();
        // LoginV2 + npi + HCP: go to /login so the remounted component shows the HCP
        // gate and the NPI form — matching prod behavior.
        if (loginV2NpiUsesLegacyHcpGate(this.props.settings)) {
          this.props.history.push('/login');
        } else {
          this.props.history.push('/');
        }
        break;

      case 'exit':
        window.location.assign(this.props.settings.page.redirectLink);
        break;

      default:
        break;
    }
  }

  /**
   * Initial visibility for legacy `LoginModal` (HCP gate). Aligns `authenticate` with
   * `loginV2.authenticate` via `requiresAuthentication`. When LoginV2 + npi + HCP on
   * (`loginV2NpiUsesLegacyHcpGate`), show the gate on every route so users cannot reach
   * any page (/, /login, /faq, …) without HCP confirmation first.
   */
  computeInitialShowLoginModal(settings, pathname, authenticated) {
    if (authenticated) {
      return false;
    }
    if (settings.loginModal?.disabled) {
      return false;
    }
    const needsAuth = requiresAuthentication(settings);
    if (!needsAuth) {
      return true;
    }
    if (loginV2NpiUsesLegacyHcpGate(settings)) {
      return true;
    }
    return pathname === '/login';
  }

  componentDidMount() {
    // Delaying modal display so Aphrodite can apply styles first, avoiding style flicker
    const {settings, authenticated} = this.props;
    const {location} = this.props;

    // Consume the one-shot flag set by _modalHandler('hideLogin'). Clearing it here means
    // a subsequent page refresh will NOT see the flag and will show the HCP gate again,
    // satisfying the requirement that every fresh load (refresh / paste URL) gates the user.
    if (sessionStorage.getItem('hcpJustAccepted')) {
      sessionStorage.removeItem('hcpJustAccepted');
      return;
    }

    const showLoginModal = this.computeInitialShowLoginModal(
      settings,
      location.pathname,
      authenticated
    );

    setTimeout(() => {
      this.setState({showLoginModal});
    }, 5);
  }

  componentDidUpdate(prevProps) {
    // Keep legacy HCP modal state in sync with route/auth transitions:
    // 1) If auth just became true, force-close the modal so it does not reopen after /login -> /.
    // 2) While unauthenticated, recompute visibility only when pathname changes
    //    (e.g. / -> /login after HCP accept, direct /login loads, or back/forward nav).
    // 3) Delay state update slightly to avoid Aphrodite style flicker during route transitions.
    const {authenticated, location, settings} = this.props;

    if (authenticated) {
      if (!prevProps.authenticated && this.state.showLoginModal) {
        this.setState({showLoginModal: false});
      }
      return;
    }
    if (prevProps.location.pathname === location.pathname) {
      return;
    }
    const showLoginModal = this.computeInitialShowLoginModal(
      settings,
      location.pathname,
      authenticated
    );
    setTimeout(() => {
      this.setState({showLoginModal});
    }, 5);
  }

  render() {
    const {
      location,
      authenticated,
      settings,
      styleSheet,
    } = this.props;

    const {showLoginModal, showRedirectModal, isIsiTrayExpanded} = this.state;

    const currentRoute = location.pathname;
    const useLoginV2Workflow = hasLoginV2Workflow(settings);
    const showLoginV2OverlayModals = useLoginV2Workflow && !loginV2NpiUsesLegacyHcpGate(settings);
    const showLegacyLoginModals = !useLoginV2Workflow || loginV2NpiUsesLegacyHcpGate(settings);

    if (authenticated && currentRoute === '/login') {
      // Remove the token param from the search string without re-encoding it.
      // URLSearchParams.toString() encodes [] as %5B%5D, which breaks bracket-format
      // array params (e.g. ?state[]=AK) used by coverage filter URL restoration.
      const rawSearch = this.props.location.search || '';
      const parts = rawSearch.replace(/^\?/, '').split('&').filter((p) => p && !p.startsWith('token='));
      const search = parts.length > 0 ? `?${parts.join('&')}` : '';

      return (<Redirect to={{
        pathname: '/',
        search: search,
        state: {from: this.props.location}}}
      />);
    }

    return (
      <div>
        <a href="#main-content" className="visually-hidden-focusable">Skip to main content</a>
        <LinkTrackingListener />
        {/* LoginV2 workflow: LoginModals (HCP + npiModal). Skipped when npi + HCP uses legacy LoginModal. */}
        {showLoginV2OverlayModals && !authenticated && (
          <>
            {currentRoute === '/' && (
              <LoginModals key="login-modals" styleSheet={styleSheet} />
            )}
            {currentRoute === '/login' && isNpiModalType(settings) && (
              <LoginModals key="login-modals-login-route" styleSheet={styleSheet} />
            )}
          </>
        )}

        {/* Legacy workflow, or LoginV2 + npi + HCP on: LoginModal + RedirectModal */}
        {showLegacyLoginModals && (
          <>
            <LoginModal
              settings={settings}
              styleSheet={styleSheet}
              showLoginModal={showLoginModal && !authenticated}
              handleHide={() => this._modalHandler('hideLogin')}
              handleRedirect={() => this._modalHandler('redirect')}
            />
            {showRedirectModal && <RedirectModal
              settings={settings}
              styleSheet={styleSheet}
              showRedirectModal={showRedirectModal}
              handleExit={() => this._modalHandler('exit')}
            />}
          </>
        )}

        <Header
          logoAssets={logoAssets}
          settings={settings}
          styleSheet={styleSheet}
          authenticated={authenticated}
          handleLogout={() => this._modalHandler('logout')}
          expandBoxedIsi={() => this.updateIsiTrayExpanded(true)}
        />

        {/* Render Login component on /login route for legacy workflow or LoginV2 workflow non-modal types */}
        {currentRoute === '/login' && (
          (!useLoginV2Workflow || (useLoginV2Workflow && !isNpiModalType(settings))) && (
            <Login styleSheet={styleSheet}/>
          )
        )}
        {currentRoute === '/faq' && <Faq styleSheet={styleSheet}/>}
        {currentRoute === '/' && (
          settings.table?.dynamicColumns && settings.table?.useNewDynamicComponent ?
            <DynamicCoverage styleSheet={styleSheet}/> :
            <Coverage styleSheet={styleSheet}/>
        )}

        <Footer
          settings={settings}
          styleSheet={styleSheet}
          logoAssets={logoAssets}
          toggleExpandBoxedIsi={this.toggleExpandBoxedIsi}
          isBoxedIsiExpanded={isIsiTrayExpanded}
        />
      </div>
    );
  }
}

PalPage.propTypes = {
  authenticated: PropTypes.bool,
  history: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
  styleSheet: PropTypes.object.isRequired,
  dispatchLogout: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {login, settings} = state;

  return {
    authenticated: login.get('authenticated'),
    settings: settings.get('settings').toJS(),
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    dispatchLogout: () => dispatch(logout()),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withRouter(PalPage));
